import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';

type ConnectionStatus = 'open' | 'closed' | 'trying' | 'slow';

export interface EventHandler {
  eventName: string;
  handler: (...args: any[]) => void;
}

@Injectable({
  providedIn: 'root',
})

export class SignalRService {
  public signalRConnectionStatus: BehaviorSubject<ConnectionStatus> = new BehaviorSubject<ConnectionStatus>('closed');
  public messageReceived = new BehaviorSubject<string>('');
  public connected: ConnectionStatus = 'closed';
  private adminUrl: string = "";
  private groupNames: string[] = [];
  private eventHandlers: EventHandler[] = [];
  private eventSubjects: Map<string, Subject<any>> = new Map();
  userId: string = "";
  private readonly http: HttpClient = inject(HttpClient);

  private SetUrl(url: string) {
    this.adminUrl = url;
  }

  private AddGroupName(groupName: string) {
    if (!this.groupNames.includes(groupName)) {
      this.groupNames.push(groupName);
    }
  }

  public hubConnection?: signalR.HubConnection;

  public async startConnection(url: string, groupName: string | string[]) {
    this.SetUrl(url ?? this.adminUrl);
    const groups = Array.isArray(groupName) ? groupName : [groupName];
    groups.forEach(group => this.AddGroupName(group));
    const maxRetries = 10;
    let attempt = 0;

    const tryNegotiate = () => {
      this.http.post(this.adminUrl + "/signalr/negotiate", "").subscribe({
        next: res => {
          this.userId = res['userId'];
          const url = res['url'];
          const accessToken = res['accessToken'];
          this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(url, {
              accessTokenFactory: () => accessToken
            })
            .withKeepAliveInterval(2000)
            .withAutomaticReconnect()
            .build();

          const tryStartConnection = () => {
            this.hubConnection!.start().then(() => {
              this.connected = "open";
              this.signalRConnectionStatus.next(this.connected);
              this.getSignalRGroup();
            }).catch((error) => {
              console.error('Connection failed to start:', error);
              this.connected = "closed";
              this.signalRConnectionStatus.next(this.connected);
            });
          };

          try {
            tryStartConnection();

            this.hubConnection.on('connectionSlow', () => {
              this.connected = "slow";
              this.signalRConnectionStatus.next(this.connected);
            });

            this.hubConnection.onclose((error) => {
              if (error) {
                this.connected = "closed";
                console.error("Connection closed with error:", error);
                this.signalRConnectionStatus.next(this.connected);
              } else {
                this.connected = "closed";
                this.signalRConnectionStatus.next(this.connected);
              }
            });

            this.hubConnection.onreconnecting(() => {
              this.connected = "trying";
            });

            this.hubConnection.onreconnected(() => {
              this.connected = "open";
              this.signalRConnectionStatus.next(this.connected);
              this.rejoinGroups();
              this.reconnectEventHandlers();
            });
            const out = { userId: this.userId };
            this.handleDisconnects();
            return out;
          } catch {
            this.connected = "closed";
            this.signalRConnectionStatus.next(this.connected);
            return false;
          }
        },
        error: err => {
          attempt++;
          console.error(`Negotiate failed (attempt ${attempt}):`, err);
          if (attempt < maxRetries) {
            setTimeout(() => tryNegotiate(), 2000);
          } else {
            console.error(`SignalR negotiate failed after ${maxRetries} attempts.`);
            this.connected = "closed";
            this.signalRConnectionStatus.next(this.connected);
          }
        }
      });
    };

    tryNegotiate();
  }

  handleDisconnects() {
    this.hubConnection?.onclose(() => {
      this.connected = "closed";
      setTimeout(() => this.startConnection(this.adminUrl, this.groupNames), 3000);
      this.signalRConnectionStatus.next(this.connected);
    });
  }

  private connectToGroups() {
    this.hubConnection?.on('newMessage', (user, message) => {
      this.messageReceived.next(`${user}: ${message}`);
    });
  }

  private rejoinGroups() {
    this.groupNames.forEach(group => {
      this.http.post(`${this.adminUrl}/signalr/AddToGroup/${group}`, "").subscribe();
    });
  }

  private reconnectEventHandlers() {
    this.eventHandlers.forEach(handler => {
      this.hubConnection?.on(handler.eventName, handler.handler);
    });
  }

  public registerEventHandler(eventName: string, handler: (...args: any[]) => void): void {
    const existingHandler = this.eventHandlers.find(h => h.eventName === eventName);
    if (!existingHandler) {
      this.eventHandlers.push({ eventName, handler });
      this.hubConnection?.on(eventName, handler);
    }
  }

  public unregisterEventHandler(eventName: string): void {
    const index = this.eventHandlers.findIndex(h => h.eventName === eventName);
    if (index !== -1) {
      this.eventHandlers.splice(index, 1);
      this.hubConnection?.off(eventName);
    }
  }

  public getEventObservable<T>(eventName: string): Subject<T> {
    if (!this.eventSubjects.has(eventName)) {
      const subject = new Subject<T>();
      this.eventSubjects.set(eventName, subject);

      this.registerEventHandler(eventName, (...args: any[]) => {
        subject.next(args.length === 1 ? args[0] : args);
      });
    }
    return this.eventSubjects.get(eventName) as Subject<T>;
  }

  public async joinGroup(groupName: string): Promise<void> {
    this.AddGroupName(groupName);
    if (this.connected === 'open') {
      return new Promise((resolve, reject) => {
        this.http.post(`${this.adminUrl}/signalr/AddToGroup/${groupName}`, "").subscribe({
          next: () => resolve(),
          error: (err) => reject(err)
        });
      });
    }
  }

  public async sendMessage(user: string, message: string) {
    if (this.hubConnection) {
        await this.hubConnection.invoke('SendMessage', user, message);
    }
  }

  public stopConnection() {
    this.hubConnection?.stop();
  }

  public async getSignalRGroup() {
    this.groupNames.forEach(group => {
      this.http.post(`${this.adminUrl}/signalr/AddToGroup/${group}`, "").subscribe();
    });
  }
}
